import {
	INCOME_VERIFICATION_TYPES,
	LOAN_PURPOSE_TYPES,
	YES_NO,
	CITIZENSHIP,
	BANKRUPTCY_TYPE,
	HOUSING_EVENT_TYPE,
	HOUSE_EVENT_SEASONING,
	BANKRUPTCY_OUTCOME,
	BANKRUPTCY_SEASONING,
	TERMS,
	AMORTIZATION_TYPES,
	ARM_FIXED_TERMS,
	DESIRED_LOCK_PERIOD,
	LENDER_VS_BORROW,
	PRODUCT_TYPES,
	LOAN_TYPES,
	LIEN_TYPES,
	PREPAYMENT_PENALTY_REQUIRED,
	PREPAYMENT_PENALTY_OPTIONAL,
	ESCROW_WAIVER,
	PROPERTY_TYPE,
	OCCUPANCY,
	PROPERTY_UNITS,
} from "../../../../utils/quickPricerConstants";
import { calculateLtvCltv } from "../../../../utils/ltvCltvCalculator";
import { VERBIAGE_CONSTANTS } from "../../../../utils/verbiage.constants";

export const PRODUCT_INPUTS_FORM_CONFIG = {
	sections: [
		{
			name: "loanInformation",
			title: "Loan Information",
			fields: [
				{
					name: "incomeVerificationType",
					label: "Income Verification Type",
					component: "select",
					options: INCOME_VERIFICATION_TYPES,
					required: true,
					disabled: true,
					syncedField: true,
				},
				{
					// RF-34 / PathA-3.04: calculated by Encompass from the saved loan (field 742),
					// read on Step B entry — never typed. Greyed on DSCR products.
					name: "debtToIncomeRatio",
					label: "Debt-to-Income Ratio (DTI)",
					component: "masked",
					maskType: "percentage",
					placeholder: "0.000",
					required: false,
					disabled: true,
					info: (s) => {
						const t = s.incomeVerificationType;
						return t === "InvestorDscr" || t === "InvestorNoRatio" ? "" : VERBIAGE_CONSTANTS.DTI_INFO;
					},
					// Shown beneath the field when Encompass has no DTI for the loan yet.
					note: (s) => {
						const t = s.incomeVerificationType;
						if (t === "InvestorDscr" || t === "InvestorNoRatio") return "";
						return s.debtToIncomeRatio ? "" : VERBIAGE_CONSTANTS.DTI_NOT_CALCULATED;
					},
				},
				{
					name: "debtServiceCoverageRatio",
					label: "Debt Service Coverage Ratio (DSCR)",
					component: "masked",
					maskType: "ratio",
					placeholder: "0.00",
					required: (s) => {
						const t = s.incomeVerificationType;
						return t === "InvestorDscr" || t === "InvestorNoRatio";
					},
					disabled: (s) => {
						const t = s.incomeVerificationType;
						return !(t === "InvestorDscr" || t === "InvestorNoRatio");
					},
					info: (s) => {
						const t = s.incomeVerificationType;
						return t === "InvestorDscr" || t === "InvestorNoRatio" ? VERBIAGE_CONSTANTS.DSCR_INFO : "";
					},
				},
				{
					name: "loanPurpose",
					label: "Loan Purpose",
					component: "select",
					options: LOAN_PURPOSE_TYPES,
					required: true,
					xmlField: true,
				},
				{
					name: "purchasePrice",
					label: "Purchase Price",
					component: "masked",
					maskType: "currency",
					placeholder: "$",
					xmlField: true,
				},
				{
					name: "estimatedValue",
					label: "Estimated / Appraised Value",
					component: "masked",
					maskType: "currency",
					placeholder: "$",
					required: true,
					xmlField: true,
				},
				{
					name: "loanAmount",
					label: "Loan Amount",
					component: "masked",
					maskType: "currency",
					placeholder: "$",
					required: true,
					xmlField: true,
				},
				{
					name: "closedEndSecondLienAmt",
					label: "Closed End Second Lien Amt",
					component: "masked",
					maskType: "currency",
					placeholder: "$",
				},
				{
					name: "helocLineAmount",
					label: "HELOC Line Amount",
					component: "masked",
					maskType: "currency",
					placeholder: "$",
				},
				{
					name: "helocDrawnAmount",
					label: "HELOC Drawn Amount",
					component: "masked",
					maskType: "currency",
					placeholder: "$",
				},
				{
					name: "cashoutAmount",
					label: "Cashout Amount",
					component: "masked",
					maskType: "currency",
					placeholder: "$",
					required: (s) => s.loanPurpose === "RefiCashout",
				},
				{
					name: "ltv",
					label: "LTV",
					component: "masked",
					maskType: "percentage",
					placeholder: "%",
					disabled: true,
					computed: true,
					compute: (s) =>
						calculateLtvCltv({
							loanPurpose: s.loanPurpose,
							purchasePrice: s.purchasePrice,
							estimatedValue: s.estimatedValue,
							loanAmount: s.loanAmount,
							closedEndSecondLienAmt: s.closedEndSecondLienAmt,
							helocDrawnAmount: s.helocDrawnAmount,
							helocLineAmount: s.helocLineAmount,
							cashoutAmount: s.cashoutAmount,
						}).ltv,
				},
				{
					name: "cltv",
					label: "CLTV",
					component: "masked",
					maskType: "percentage",
					placeholder: "%",
					disabled: true,
					computed: true,
					compute: (s) =>
						calculateLtvCltv({
							loanPurpose: s.loanPurpose,
							purchasePrice: s.purchasePrice,
							estimatedValue: s.estimatedValue,
							loanAmount: s.loanAmount,
							closedEndSecondLienAmt: s.closedEndSecondLienAmt,
							helocDrawnAmount: s.helocDrawnAmount,
							helocLineAmount: s.helocLineAmount,
							cashoutAmount: s.cashoutAmount,
						}).cltv,
				},
				{
					name: "hcltv",
					label: "HCLTV",
					component: "masked",
					maskType: "percentage",
					placeholder: "%",
					disabled: true,
					computed: true,
					compute: (s) =>
						calculateLtvCltv({
							loanPurpose: s.loanPurpose,
							purchasePrice: s.purchasePrice,
							estimatedValue: s.estimatedValue,
							loanAmount: s.loanAmount,
							closedEndSecondLienAmt: s.closedEndSecondLienAmt,
							helocDrawnAmount: s.helocDrawnAmount,
							helocLineAmount: s.helocLineAmount,
							cashoutAmount: s.cashoutAmount,
						}).hcltv,
				},
				{
					name: "interestOnly",
					label: "Interest Only",
					component: "select",
					options: YES_NO,
				},
				{
					name: "prePaymentPenalty",
					label: "Prepayment Penalty",
					component: "select",
					options: (s) => {
						const t = s.incomeVerificationType;
						const o = s.occupancy;
						const isRequired =
							t === "InvestorDscr" ||
							t === "InvestorNoRatio" ||
							o === "InvestmentProperty";
						return isRequired ? PREPAYMENT_PENALTY_REQUIRED : PREPAYMENT_PENALTY_OPTIONAL;
					},
					required: (s) => {
						const t = s.incomeVerificationType;
						const o = s.occupancy;
						return (
							t === "InvestorDscr" ||
							t === "InvestorNoRatio" ||
							o === "InvestmentProperty"
						);
					},
				},
				{
					name: "escrowWaiver",
					label: "Escrow Waiver",
					component: "select",
					options: ESCROW_WAIVER,
					required: true,
				},
			],
		},
		{
			name: "borrowerInformation",
			title: "Borrower Information",
			fields: [
				{
					name: "citizenship",
					label: "Citizenship",
					component: "select",
					options: CITIZENSHIP,
					required: true,
				},
				{
					name: "firstTimeHomeBuyer",
					label: "First Time Homebuyer",
					component: "select",
					options: YES_NO,
					required: true,
				},
				{
					name: "nonoccupantCoborrower",
					label: "Non-Occupant Co-borrower",
					component: "select",
					options: YES_NO,
				},
				{
					name: "selfEmployed",
					label: "Self Employed",
					component: "select",
					options: YES_NO,
					required: true,
				},
				{
					name: "reserve",
					label: "Reserve (Months)",
					component: "numeric",
					placeholder: "0",
					required: true,
				},
				{
					name: "financedProperties",
					label: "Financed Properties",
					component: "numeric",
					placeholder: "0",
					required: true,
				},
			],
		},
		{
			name: "creditProfile",
			title: "Credit Profile",
			fields: [
				{
					name: "qualifyingCreditScore",
					label: "Qualifying Credit Score (FICO)",
					component: "numeric",
					placeholder: "0",
					required: true,
					isFullWidth: true,
					creditReissueField: true,
				},
				{
					name: "housingEventType",
					label: "Housing Event Type",
					component: "select",
					// Forbearance has no Encompass CX.HOUSINGEVENTTYPE value, so it could never be saved.
					options: HOUSING_EVENT_TYPE.filter((o) => o.value !== "Forbearance"),
					required: true,
					creditReissueField: true,
				},
				{
					name: "housingEventSeasoning",
					label: "Housing Event Seasoning",
					component: "select",
					options: HOUSE_EVENT_SEASONING,
					required: true,
				},
				{
					name: "bankruptcyType",
					label: "Bankruptcy Type",
					component: "select",
					options: BANKRUPTCY_TYPE,
					required: true,
					creditReissueField: true,
				},
				{
					name: "bankruptcyOutcome",
					label: "Bankruptcy Outcome",
					component: "select",
					options: BANKRUPTCY_OUTCOME,
					required: true,
				},
				{
					name: "bankruptcySeasoning",
					label: "Bankruptcy Seasoning",
					component: "select",
					options: BANKRUPTCY_SEASONING,
					required: true,
				},
				{
					name: "mortgageLatePayments",
					label: "Mortgage Late Payments?",
					component: "select",
					options: YES_NO,
					required: true,
					creditReissueField: true,
				},
				{
					name: "descriptionOfMortgage",
					descriptionSection:
						"Enter the number of mortgage payment delinquencies within each period:",
					visibleIf: (s) => s.mortgageLatePayments === "Yes",
				},
				{
					name: "Mtg_Lates_x30_12",
					label: "Mtg Lates ×30 (12 mos.)",
					component: "numeric",
					placeholder: "0",
					visibleIf: (s) => s.mortgageLatePayments === "Yes",
				},
				{
					name: "Mtg_Lates_x30_13_24",
					label: "Mtg Lates ×30 (13–24 mos.)",
					component: "numeric",
					placeholder: "0",
					visibleIf: (s) => s.mortgageLatePayments === "Yes",
				},
				{
					name: "Mtg_Lates_x60_12",
					label: "Mtg Lates ×60 (12 mos.)",
					component: "numeric",
					placeholder: "0",
					visibleIf: (s) => s.mortgageLatePayments === "Yes",
				},
				{
					name: "Mtg_Lates_x60_13_24",
					label: "Mtg Lates ×60 (13–24 mos.)",
					component: "numeric",
					placeholder: "0",
					visibleIf: (s) => s.mortgageLatePayments === "Yes",
				},
				{
					name: "Mtg_Lates_x90_12",
					label: "Mtg Lates ×90 (12 mos.)",
					component: "numeric",
					placeholder: "0",
					visibleIf: (s) => s.mortgageLatePayments === "Yes",
				},
				{
					name: "Mtg_Lates_x90_13_24",
					label: "Mtg Lates ×90 (13–24 mos.)",
					component: "numeric",
					placeholder: "0",
					visibleIf: (s) => s.mortgageLatePayments === "Yes",
				},
				{
					name: "Mtg_Lates_x120_12",
					label: "Mtg Lates ×120 (12 mos.)",
					component: "numeric",
					placeholder: "0",
					visibleIf: (s) => s.mortgageLatePayments === "Yes",
				},
				{
					name: "Mtg_Lates_x120_13_24",
					label: "Mtg Lates ×120 (13–24 mos.)",
					component: "numeric",
					placeholder: "0",
					visibleIf: (s) => s.mortgageLatePayments === "Yes",
				},
			],
		},
		{
			name: "propertyInformation",
			title: "Property Information",
			fields: [
				{
					name: "propertyType",
					label: "Property Type",
					component: "select",
					options: PROPERTY_TYPE,
					required: true,
					xmlField: true,
				},
				{
					name: "occupancy",
					label: "Occupancy",
					component: "select",
					options: OCCUPANCY,
					required: true,
					xmlField: true,
				},
				{
					name: "shortTermRental",
					label: "Short Term Rental",
					component: "select",
					options: YES_NO,
					required: true,
				},
				{
					name: "numberOfUnits",
					label: "Number of Units",
					component: "select",
					options: PROPERTY_UNITS,
					required: true,
					xmlField: true,
				},
				{
					name: "numberOfStories",
					label: "Number of Stories",
					component: "numeric",
					placeholder: "1",
					required: true,
					xmlField: true,
				},
				{
					name: "state",
					label: "State",
					component: "readonly",
					disabled: true,
					xmlField: true,
				},
				{
					name: "county",
					label: "County",
					component: "readonly",
					disabled: true,
					xmlField: true,
				},
				{
					name: "city",
					label: "City",
					component: "readonly",
					disabled: true,
					xmlField: true,
				},
				{
					name: "zipcode",
					label: "Zipcode",
					component: "numeric",
					placeholder: "0",
					required: true,
				},
			],
		},
		{
			name: "productPricingFilters",
			title: "Product & Pricing Search Filters",
			fields: [
				{
					name: "loanTerms",
					label: "Loan Terms (Years)",
					component: "autocomplete-multi",
					options: TERMS,
					placeholder: "Search here...",
					required: true,
				},
				{
					name: "amortizationTypes",
					label: "Amortization Types",
					component: "autocomplete-multi",
					options: AMORTIZATION_TYPES,
					placeholder: "Search here...",
					required: true,
				},
				{
					name: "ARMFixedTerms",
					label: "ARM Fixed Terms",
					component: "autocomplete-multi",
					options: ARM_FIXED_TERMS,
					placeholder: "Search here...",
					required: (s) =>
						Array.isArray(s.amortizationTypes) &&
						s.amortizationTypes.includes("ARM"),
				},
				{
					name: "desiredRate",
					label: "Desired Rate",
					component: "masked",
					maskType: "percentage",
					placeholder: "0.000",
				},
				{
					name: "desiredPrice",
					label: "Desired Price",
					component: "masked",
					maskType: "ratio",
					placeholder: "0",
				},
				{
					name: "desiredLockPeriod",
					label: "Desired Lock Period",
					component: "select",
					options: DESIRED_LOCK_PERIOD,
				},
				{
					name: "lenderPaidvsBorrowerPaid",
					label: "Lender Paid (LPC) vs. Borrower Paid (BPC)",
					component: "select",
					options: LENDER_VS_BORROW,
					required: true,
				},
				{
					name: "productTypes",
					label: "Product Types",
					component: "autocomplete-multi",
					options: PRODUCT_TYPES,
					placeholder: "Search here...",
					disabled: true,
				},
				{
					name: "loanType",
					label: "Loan Type",
					component: "autocomplete-multi",
					options: LOAN_TYPES,
					placeholder: "Search here...",
					disabled: true,
				},
				{
					name: "piLienType",
					label: "Lien Type",
					component: "select",
					options: LIEN_TYPES,
					disabled: true,
				},
			],
		},
	],
};
